import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * KioskScreensaverComponent
 *
 * Full-screen idle "attract" screensaver for the 42" kiosk. It replaces the
 * blank Windows screensaver with an on-brand, always-messaging idle screen so a
 * passer-by immediately understands what the panel does and that a single tap
 * wakes it.
 *
 * Page-aware: the shell feeds it the identity of whatever page was open
 * ({@link pageLabel} / {@link pageTagline} / {@link pageIcon}) so the idle
 * screen reads "Quote", "Schedule", etc. The signed-in tenant's company logo
 * gently breathes in and out over the cycling welcome imagery.
 *
 * Purely presentational: the shell decides *when* to show it (after N seconds
 * of no interaction) and listens for {@link wake} to dismiss it. Honours
 * `prefers-reduced-motion`.
 */
@Component({
    selector: 'kiosk-screensaver',
    templateUrl: './kiosk-screensaver.component.html',
    styleUrls: ['./kiosk-screensaver.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, MatIconModule],
})
export class KioskScreensaverComponent implements OnInit, OnDestroy {
    /** Identity of the page behind the screensaver (e.g. "Quote", "Schedule"). */
    @Input() pageLabel = '';
    /** Short supporting line under the page label. */
    @Input() pageTagline = '';
    /**
     * Show the customer apparel slideshow behind the overlay. Only the
     * customer-facing Quote page opts in; staff stations (Schedule, Packages)
     * get a clean branded backdrop instead.
     */
    @Input() showImagery = false;

    /** Fired on the first tap/press ? the shell dismisses us. */
    @Output() wake = new EventEmitter<void>();

    /** Welcome-screen slideshow: groups wearing custom printed apparel. */
    readonly slides: readonly string[] = [
        '/images/kiosk/kiosk-attract-reunion.png',
        '/images/kiosk/kiosk-attract-corporate.png',
        '/images/kiosk/kiosk-attract-marathon.png',
    ];
    index = 0;

    /**
     * Brand mark that breathes over the imagery. Defaults to the District
     * Printing wordmark, but swaps to the signed-in trader's `company_logo`
     * (read from `userData`) so each tenant sees their own brand ? mirroring
     * the idle-workspace dashboard watermark.
     */
    companyLogoSrc = 'images/logo/dp_logo.png';
    companyLogoIsAnimated = false;
    private readonly _fallbackLogo = 'images/logo/dp_logo.png';

    private _timer: ReturnType<typeof setInterval> | null = null;
    private readonly _reduceMotion =
        typeof matchMedia !== 'undefined' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches;

    constructor(private _cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        this._resolveCompanyLogo();

        if (this.showImagery && !this._reduceMotion) {
            this._timer = setInterval(() => {
                this.index = (this.index + 1) % this.slides.length;
                this._cdr.markForCheck();
            }, 6000);
        }
    }

    ngOnDestroy(): void {
        if (this._timer !== null) {
            clearInterval(this._timer);
            this._timer = null;
        }
    }

    /** Any tap/press on the overlay wakes the kiosk. */
    onWake(): void {
        this.wake.emit();
    }

    /** Broken tenant logo URL ? fall back to the DP wordmark. */
    onLogoError(): void {
        if (this.companyLogoSrc !== this._fallbackLogo) {
            this.companyLogoSrc = this._fallbackLogo;
            this.companyLogoIsAnimated = false;
            this._cdr.markForCheck();
        }
    }

    private _resolveCompanyLogo(): void {
        try {
            const raw =
                typeof localStorage !== 'undefined'
                    ? localStorage.getItem('userData')
                    : null;
            if (!raw || raw === 'null' || raw === 'undefined') {
                return;
            }
            const data = JSON.parse(raw);
            const logo = data?.company_logo || data?.companyLogo;
            if (logo && typeof logo === 'string') {
                this.companyLogoSrc = logo;
                this.companyLogoIsAnimated = /\.gif(\?|#|$)/i.test(logo);
            }
        } catch {
            // Private-mode storage / parse error ? keep the default mark.
        }
    }
}
